/**
 * Core data service for TRAC India indicator data
 * Supports multi-year, multi-source indicators with state and district-level data
 * Updated to load CSV files directly
 */

import { shapeNameToStateCode } from "../config/stateMapping";
import { parseCSV, normalizeValue } from "../utils/csvParser";

// Caches
const csvCache = new Map();
let manifestCache = null;

// Utility functions
const normalizeString = (value) => {
  if (value == null) return "";
  return String(value)
    .replace(/^\uFEFF/, "")
    .trim();
};

const normalizeIndicatorKey = (value) => {
  return normalizeString(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const normalizeStateName = (name) => {
  const stripDiacritics = (text) => {
    try {
      return text.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    } catch {
      return text;
    }
  };

  const s = stripDiacritics(normalizeString(name))
    .replace(/\s+/g, " ")
    .replace(/\u00A0/g, " ")
    .trim();

  return s
    .replace(/&/g, "and")
    .replace(/\s+and\s+/gi, " and ")
    .replace(/\s+/g, " ")
    .trim();
};

const stateNameToCode = (name) => {
  if (!name) return null;
  const raw = normalizeString(name);
  const norm = normalizeStateName(raw);

  if (shapeNameToStateCode[raw]) return shapeNameToStateCode[raw];
  if (shapeNameToStateCode[norm]) return shapeNameToStateCode[norm];

  const alt = norm.replace(/\./g, "").replace(/\s+/g, " ").trim();
  if (shapeNameToStateCode[alt]) return shapeNameToStateCode[alt];

  return alt
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim();
};

const normalizeResidenceToTRU = (residence) => {
  const r = normalizeString(residence).toLowerCase();
  if (r === "rural") return "Rural";
  if (r === "urban") return "Urban";
  return "Total";
};

const inferRowLevel = (row) => {
  const explicit = normalizeString(row.Level || row.level).toLowerCase();
  if (explicit) return explicit;

  const districtId = normalizeString(row.District || row.district);
  const stateId = normalizeString(row.State || row.state);
  const name = normalizeString(row.Name || row.name).toLowerCase();

  if (districtId && districtId !== "0") return "district";
  if (stateId && stateId !== "0" && name !== "india") return "state";
  if (name === "india") return "india";
  return "";
};

const districtNameToKey = (name) => {
  return normalizeString(name)
    .toLowerCase()
    .replace(/\([^)]*\)/g, "")
    .replace(/\bdistrict\b/g, "")
    .replace(/\bdist\.?\b/g, "")
    .replace(/^the\s+/g, "")
    .replace(/[^a-z0-9\s-]/g, " ")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim();
};

// Core data loading
export const loadManifest = async () => {
  if (manifestCache) return manifestCache;

  try {
    const response = await fetch("/json/indicator_manifest.json", {
      cache: "no-store",
    });
    if (!response.ok) {
      console.warn("Failed to load indicator manifest");
      return { indicators: [] };
    }
    manifestCache = await response.json();
    return manifestCache;
  } catch (err) {
    console.error("Error loading manifest:", err);
    return { indicators: [] };
  }
};

const loadCSVFile = async (fileName) => {
  if (csvCache.has(fileName)) return csvCache.get(fileName);

  const promise = (async () => {
    try {
      const response = await fetch(`/data/${fileName}`);
      if (!response.ok) {
        console.warn(`Failed to load CSV file: ${fileName}`);
        return { headers: [], rows: [] };
      }
      const csvText = await response.text();
      return parseCSV(csvText);
    } catch (err) {
      console.error(`Error loading CSV file ${fileName}:`, err);
      return { headers: [], rows: [] };
    }
  })();

  csvCache.set(fileName, promise);
  return promise;
};

// Main API functions
export const getIndicatorData = async (
  indicatorName,
  source,
  year,
  { residence = "total" } = {},
) => {
  if (!indicatorName) return {};

  const manifest = await loadManifest();
  // Match by name, source, AND year since we now have multiple entries per indicator
  const manifestEntry = manifest.indicators.find(
    (ind) =>
      ind.name === indicatorName && ind.source === source && ind.year === year,
  );

  if (!manifestEntry || !manifestEntry.dataFile) {
    console.warn(`No data found for: ${indicatorName} (${source} ${year})`);
    return {};
  }

  const tru = normalizeResidenceToTRU(residence);
  const stateMap = {};

  // Load the data CSV file that contains this indicator
  const csvData = await loadCSVFile(manifestEntry.dataFile);
  if (csvData && csvData.rows) {
    // Process each row in the CSV
    for (const row of csvData.rows) {
      const level = inferRowLevel(row);
      if (level !== "state") continue;

      const rowTRU = normalizeString(row.TRU || row.tru || "Total");
      if (tru && rowTRU !== tru) continue;

      const stateName = normalizeString(row.Name || row.name);
      if (!stateName) continue;

      const stateCode = stateNameToCode(stateName);
      if (!stateCode) continue;

      // Get the value for this specific indicator
      const value = normalizeValue(row[indicatorName]);
      if (value == null) continue;

      stateMap[stateCode] = {
        state: stateName,
        stateCode,
        value,
      };
    }
  }

  return stateMap;
};

export const getIndicatorDistrictData = async (
  indicatorName,
  stateCode,
  source,
  year,
  { residence = "total" } = {},
) => {
  if (!indicatorName || !stateCode) return {};

  const manifest = await loadManifest();
  // Match by name, source, AND year since we now have multiple entries per indicator
  const manifestEntry = manifest.indicators.find(
    (ind) =>
      ind.name === indicatorName && ind.source === source && ind.year === year,
  );

  if (!manifestEntry || !manifestEntry.dataFile) return {};

  const tru = normalizeResidenceToTRU(residence);
  const districtMap = {};
  const stateIdToCode = new Map();

  // Load the data CSV file that contains this indicator
  const csvData = await loadCSVFile(manifestEntry.dataFile);
  if (csvData && csvData.rows) {
    // Build a lookup for files where district rows store numeric state IDs
    // (e.g. `State: 23`) instead of state names.
    for (const row of csvData.rows) {
      const level = inferRowLevel(row);
      if (level !== "state") continue;

      const stateId = normalizeString(row.State || row.state);
      const stateName = normalizeString(row.Name || row.name);
      if (!stateId || !stateName) continue;

      const mappedStateCode = stateNameToCode(stateName);
      if (mappedStateCode) {
        stateIdToCode.set(stateId, mappedStateCode);
      }
    }

    // Process each row in the CSV
    for (const row of csvData.rows) {
      const level = inferRowLevel(row);
      if (level !== "district") continue;

      // District rows may store state as a name, slug-like text, or numeric ID.
      const rowStateRaw = normalizeString(
        row.State || row.state || row.State_Name || row.state_name,
      );
      const rowStateCode =
        stateIdToCode.get(rowStateRaw) || stateNameToCode(rowStateRaw);
      if (rowStateCode !== stateCode) continue;

      const rowTRU = normalizeString(row.TRU || row.tru || "Total");
      if (tru && rowTRU !== tru) continue;

      const districtName = normalizeString(row.Name || row.name);
      if (!districtName) continue;

      const value = normalizeValue(row[indicatorName]);
      if (value == null) continue;

      const districtKey = districtNameToKey(districtName);

      districtMap[districtKey] = {
        district: districtName,
        districtKey,
        value,
      };
    }
  }

  return districtMap;
};

export const computeStats = (stateMap) => {
  const values = Object.values(stateMap || {})
    .map((d) => d?.value)
    .filter((v) => typeof v === "number" && Number.isFinite(v));

  if (values.length === 0) {
    return { average: null, min: null, max: null, median: null, count: 0 };
  }

  const sorted = [...values].sort((a, b) => a - b);
  const sum = values.reduce((s, v) => s + v, 0);
  const median = sorted[Math.floor(sorted.length / 2)];

  return {
    average: sum / values.length,
    min: sorted[0],
    max: sorted[sorted.length - 1],
    median,
    count: values.length,
  };
};

// Source and year management
export const getAvailableSourceYears = async (indicatorName) => {
  if (!indicatorName) return [];

  const manifest = await loadManifest();
  if (!manifest?.indicators) return [];

  return manifest.indicators
    .filter((ind) => ind.name === indicatorName)
    .map((ind) => ({
      source: ind.source,
      year: ind.year,
      key: ind.key,
    }));
};

export const getIndicatorSourceInfo = async (indicatorName) => {
  if (!indicatorName) {
    return { sources: [], years: [] };
  }

  const combinations = await getAvailableSourceYears(indicatorName);

  const sourcesSet = new Set();
  const yearsSet = new Set();

  combinations.forEach((combo) => {
    sourcesSet.add(combo.source);
    yearsSet.add(combo.year);
  });

  return {
    sources: Array.from(sourcesSet)
      .filter((s) => s !== "Unknown")
      .sort(),
    years: Array.from(yearsSet).sort((a, b) => b.localeCompare(a)),
  };
};

// Category and indicator management
export const getIndicatorsBySector = async () => {
  const manifest = await loadManifest();
  if (!manifest?.indicators) return new Map();

  const bySectorId = new Map();
  const seenIndicators = new Set();

  manifest.indicators.forEach((ind) => {
    const sectorId = (ind.sectorId || "other").toLowerCase();
    if (!ind.name) return;

    const key = `${sectorId}|${normalizeIndicatorKey(ind.name)}`;
    if (seenIndicators.has(key)) return;
    seenIndicators.add(key);

    if (!bySectorId.has(sectorId)) {
      bySectorId.set(sectorId, []);
    }
    bySectorId.get(sectorId).push(ind.name);
  });

  bySectorId.forEach((indicators, sectorId) => {
    bySectorId.set(sectorId, indicators.sort());
  });

  return bySectorId;
};

export const getIndicatorsForCategory = async (categoryId) => {
  if (!categoryId) return [];

  const bySectorId = await getIndicatorsBySector();
  return bySectorId.get(categoryId.toLowerCase()) || [];
};

export const getIndicatorNamesForCategory = async (categoryId) => {
  if (!categoryId) return [];

  const manifest = await loadManifest();
  if (!manifest?.indicators) return [];

  const sectorId = categoryId.toLowerCase();
  const seen = new Set();
  const results = [];

  manifest.indicators.forEach((ind) => {
    if ((ind.sectorId || "").toLowerCase() !== sectorId) return;
    if (!ind.name) return;

    const key = normalizeIndicatorKey(ind.name);
    if (seen.has(key)) return;

    seen.add(key);
    results.push(ind.name);
  });

  return results;
};

export const getCategoryStats = async () => {
  const categoryIds = [
    "amenities",
    "education",
    "health",
    "wash",
    "energy",
    "gender",
    "livelihoods",
    "people",
    "nutrition",
    "climate",
    "government-schemes",
  ];

  const manifest = await loadManifest();
  if (!manifest?.indicators) return [];

  const setsBySector = new Map();

  manifest.indicators.forEach((ind) => {
    const sectorId = (ind.sectorId || "other").toLowerCase();
    if (!categoryIds.includes(sectorId)) return;
    if (!ind.name) return;

    if (!setsBySector.has(sectorId)) {
      setsBySector.set(sectorId, new Set());
    }

    setsBySector.get(sectorId).add(normalizeIndicatorKey(ind.name));
  });

  return categoryIds.map((categoryId) => ({
    categoryId,
    sector: categoryId,
    count: setsBySector.get(categoryId)?.size || 0,
  }));
};

// Backwards compatibility exports
export const getOldTracIndicatorData = getIndicatorData;
export const computeStatsFromStateMap = computeStats;
export const getIndicatorDistrictValueMap = getIndicatorDistrictData;
export const getIndicatorStateMap = getIndicatorData;
